import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import type { LucideIcon } from 'lucide-react'
import {
  Heart, HeartCrack, FileCheck, CheckCircle2, X, Bell, Loader2,
  Flame, Snowflake, AlertTriangle, AlertCircle, ClipboardCheck, ClipboardList,
  Scale, Camera, Calendar, CalendarDays, Hand, FileQuestion, CreditCard,
  RefreshCcw, Info, Sparkles, TrendingUp, Award, Target, BookOpen, Video,
  BarChart3, MessageCircle, Dumbbell, ChevronDown, Coins,
} from 'lucide-react'

export type NotifType = 'hp' | 'coins' | 'report'

interface ApiNotification {
  id: string
  type: string
  title: string
  body: string
  isRead: boolean
  sentAt: string
}

const HP_TYPES = new Set([
  'LIFE_LOST', 'LAST_LIFE', 'LAST_CHANCE',
  'FREEZE_ACTIVATED', 'STREAK_REMINDER', 'RED_ZONE_WARNING',
])
const COINS_TYPES = new Set([
  'ACHIEVEMENT_UNLOCKED', 'STREAK_MILESTONE', 'CHALLENGE_COMPLETED',
  'LEVEL_UP', 'WEEKLY_RESULTS', 'DAILY_SUMMARY', 'NEW_CHALLENGE', 'REWARD',
])

function categorize(type: string): NotifType {
  if (HP_TYPES.has(type)) return 'hp'
  if (COINS_TYPES.has(type)) return 'coins'
  return 'report'
}

// Шаблоны уведомлений хранятся в Telegram-HTML формате (<b>, <i>, <u>, <a>, <code>, <br>).
// В мини-приложении показываем их как обычный текст: тэги вырезаем, &amp;/&lt;/&gt;/&quot;/&#39;
// раскодируем, <br> заменяем на перевод строки.
function stripTelegramHtml(input: string): string {
  if (!input) return ''
  return input
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/?[a-z][^>]*>/gi, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

// Render Telegram-HTML safely as React nodes (bold/italic/etc.) using DOMParser —
// never via dangerouslySetInnerHTML, so no XSS risk. Unknown tags collapse to their
// children, links restricted to http(s)/tg schemes.
function renderTelegramHtml(input: string): ReactNode {
  if (!input) return null
  if (typeof DOMParser === 'undefined') return stripTelegramHtml(input)
  const doc = new DOMParser().parseFromString(`<div>${input}</div>`, 'text/html')
  const root = doc.body.firstChild as HTMLElement | null
  if (!root) return input
  const renderNode = (node: Node, key: string): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent
    if (node.nodeType !== Node.ELEMENT_NODE) return null
    const el = node as Element
    const children = Array.from(el.childNodes).map((c, i) => renderNode(c, `${key}-${i}`))
    switch (el.tagName.toLowerCase()) {
      case 'b':
      case 'strong':
        return <strong key={key}>{children}</strong>
      case 'i':
      case 'em':
        return <em key={key}>{children}</em>
      case 'u':
      case 'ins':
        return <u key={key}>{children}</u>
      case 's':
      case 'strike':
      case 'del':
        return <s key={key}>{children}</s>
      case 'code':
        return <code key={key} style={{ fontFamily: 'monospace', fontSize: '0.92em' }}>{children}</code>
      case 'br':
        return <br key={key} />
      case 'a': {
        const href = el.getAttribute('href') || ''
        if (!/^(https?:|tg:)/i.test(href)) return <span key={key}>{children}</span>
        return <a key={key} href={href} target="_blank" rel="noopener noreferrer">{children}</a>
      }
      default:
        return <span key={key}>{children}</span>
    }
  }
  return Array.from(root.childNodes).map((c, i) => renderNode(c, String(i)))
}

// ── Per-type icon + accent. Falls back to category icon for unknowns. ──
const TYPE_ICONS: Record<string, { Icon: LucideIcon; color: string; emoji?: string }> = {
  // HP / Streak
  STREAK_REMINDER:    { Icon: Flame,         color: '#FF6B35' },
  FREEZE_ACTIVATED:   { Icon: Snowflake,     color: '#4A9FE5' },
  LIFE_LOST:          { Icon: HeartCrack,    color: '#FF3B30' },
  LAST_LIFE:          { Icon: Heart,         color: '#FF3B30' },
  LAST_CHANCE:        { Icon: AlertTriangle, color: '#FF9500' },
  RED_ZONE_WARNING:   { Icon: AlertCircle,   color: '#FF3B30' },
  STREAK_MILESTONE:   { Icon: Flame,         color: '#FF9500' },
  // Coins / Achievements
  REWARD:             { Icon: Coins,         color: '#F59E0B' },
  ACHIEVEMENT_UNLOCKED: { Icon: Award,       color: '#FFD60A' },
  LEVEL_UP:           { Icon: TrendingUp,    color: '#34C759' },
  CHALLENGE_COMPLETED:{ Icon: Target,        color: '#AF52DE' },
  NEW_CHALLENGE:      { Icon: Target,        color: '#4A6CF7' },
  WEEKLY_RESULTS:     { Icon: BarChart3,     color: '#6C5CE7' },
  DAILY_SUMMARY:      { Icon: FileCheck,     color: '#34C759' },
  // Reports / Reminders
  DAILY_MINIMUM:      { Icon: ClipboardCheck,color: '#34C759' },
  WEEKLY_MEASUREMENTS:{ Icon: Scale,         color: '#E91E8C' },
  PHOTO_REMINDER:     { Icon: Camera,        color: '#20C997' },
  EVENT_TODAY:        { Icon: Calendar,      color: '#FF9500' },
  EVENT_TOMORROW:     { Icon: CalendarDays,  color: '#4A9FE5' },
  RETURN_AFTER_ABSENCE: { Icon: Hand,        color: '#FF9500' },
  ONBOARDING_INCOMPLETE: { Icon: ClipboardList, color: '#4A6CF7' },
  QUESTIONNAIRE_MISSING: { Icon: FileQuestion, color: '#FF9500' },
  SUBSCRIPTION_EXPIRED: { Icon: CreditCard,  color: '#FF3B30' },
  RENEWAL_REMINDER:   { Icon: RefreshCcw,    color: '#4A9FE5' },
  GENERAL:            { Icon: Info,          color: '#6B7B8D' },
  WELCOME:            { Icon: Sparkles,      color: '#6C5CE7' },
  DIARY_REMINDER:     { Icon: BookOpen,      color: '#4A6CF7' },
  NEW_LECTURE:        { Icon: Video,         color: '#AF52DE' },
  CURATOR_COMMENT:    { Icon: MessageCircle, color: '#229ED9' },
  PROGRAM_ASSIGNED:   { Icon: Dumbbell,      color: '#4A6CF7' },
}

const TYPE_META: Record<NotifType, { fallback: LucideIcon; fallbackColor: string; bg: string }> = {
  hp: {
    fallback: Heart,
    fallbackColor: '#FF3B30',
    bg: 'rgba(255,59,48,0.09)',
  },
  coins: {
    fallback: Award,
    fallbackColor: '#FF9500',
    bg: 'rgba(255,149,0,0.10)',
  },
  report: {
    fallback: FileCheck,
    fallbackColor: '#34C759',
    bg: 'rgba(52,199,89,0.09)',
  },
}

function iconFor(notif: ApiNotification): { Icon: LucideIcon; color: string; bg: string } {
  const t = TYPE_ICONS[notif.type]
  if (t) {
    return { Icon: t.Icon, color: t.color, bg: `${t.color}1A` }
  }
  const cat = categorize(notif.type)
  const meta = TYPE_META[cat]
  return { Icon: meta.fallback, color: meta.fallbackColor, bg: meta.bg }
}

function pluralizeDays(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'день'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
  return 'дней'
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffSec = Math.floor((now.getTime() - d.getTime()) / 1000)
  if (diffSec < 60) return 'только что'
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60)
    return `${m} мин назад`
  }
  if (diffSec < 24 * 3600) {
    const h = Math.floor(diffSec / 3600)
    return `${h} ч назад`
  }
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0)
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
  if (d >= startOfYesterday && d < startOfToday) return 'Вчера'
  const days = Math.floor((startOfToday.getTime() - d.getTime()) / 86400000) + 1
  if (days < 7) return `${days} ${pluralizeDays(days)} назад`
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

function formatFullDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
}

// Threshold for triggering the "Развернуть" toggle inline.
const LONG_BODY_THRESHOLD = 180

interface NotificationsPanelProps {
  open: boolean
  onClose: () => void
}

const F = 'Roboto, sans-serif'

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const

export function NotificationsPanel({ open, onClose }: NotificationsPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  // Per-item inline expand state (id → expanded?)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  // Full-screen popup view for one notification
  const [popupNotif, setPopupNotif] = useState<ApiNotification | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: async () => {
      const { api } = await import('../services/api')
      return api.getNotifications({ limit: 50 })
    },
    enabled: open,
    staleTime: 30_000,
  })

  const markAllMutation = useMutation({
    mutationFn: async () => {
      const { api } = await import('../services/api')
      return api.markAllNotificationsRead()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    },
  })

  const markReadMutation = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const { api } = await import('../services/api')
      return api.markNotificationsRead(notificationIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    },
  })

  const openNotification = (notif: ApiNotification) => {
    setPopupNotif(notif)
    if (!notif.isRead) {
      markReadMutation.mutate([notif.id])
    }
  }

  const notifications: ApiNotification[] = data?.notifications ?? []
  const unreadCount = data?.unreadCount ?? 0
  const canMarkAll = unreadCount > 0 && !markAllMutation.isPending

  // Reset transient state when closed
  useEffect(() => {
    if (!open) {
      setExpandedIds(new Set())
      setPopupNotif(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (popupNotif) return // don't close panel when clicking outside while popup is open
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', handler)
    }
  }, [open, onClose, popupNotif])

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="notifications-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
              background: 'rgba(10,20,40,0.18)',
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
            }}
            onClick={onClose}
          />

          <motion.div
            ref={panelRef}
            data-testid="notifications-panel"
            initial={{ opacity: 0, y: -12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
            style={{
              position: 'fixed',
              top: 70,
              right: 12,
              left: 12,
              zIndex: 1001,
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)',
              borderRadius: 22,
              boxShadow: '0 20px 60px rgba(26,58,92,0.22), 0 4px 16px rgba(26,58,92,0.14), 0 0 0 1px rgba(255,255,255,0.9) inset',
              border: '1px solid rgba(255,255,255,0.7)',
              overflow: 'hidden',
              maxHeight: 'calc(100dvh - 100px)',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: F,
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px 12px',
              borderBottom: '1px solid rgba(26,58,92,0.07)',
              background: 'linear-gradient(180deg, rgba(74,108,247,0.05) 0%, transparent 100%)',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: 'linear-gradient(135deg,#4A6CF7,#6C5CE7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(74,108,247,0.32)',
                }}>
                  <Bell size={15} color="white" />
                </div>
                <div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#1C2D41' }}>Уведомления</span>
                  {unreadCount > 0 && (
                    <span data-testid="notifications-unread-badge" style={{
                      marginLeft: 7, fontSize: 10, fontWeight: 700, color: 'white',
                      background: '#FF3B30', borderRadius: 8, padding: '1px 6px',
                      verticalAlign: 'middle',
                    }}>
                      {unreadCount} новых
                    </span>
                  )}
                </div>
              </div>
              <button
                data-testid="notifications-close"
                onClick={onClose}
                style={{
                  width: 30, height: 30, borderRadius: 10,
                  background: 'rgba(26,58,92,0.07)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', padding: 0,
                }}
              >
                <X size={15} color="#6B7B8D" />
              </button>
            </div>

            {/* List */}
            <div style={{ overflowY: 'auto', padding: '10px 8px 12px', flexGrow: 1, minHeight: 80 }}>
              {isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0', color: '#9AA5B4' }}>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
                </div>
              )}
              {!isLoading && notifications.length === 0 && (
                <div data-testid="notifications-empty" style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '28px 16px', textAlign: 'center', color: '#9AA5B4',
                }}>
                  <Bell size={28} color="#C4CDD6" />
                  <p style={{ fontSize: 13, margin: '8px 0 2px', color: '#6B7B8D', fontWeight: 600 }}>Пока нет уведомлений</p>
                  <p style={{ fontSize: 11, margin: 0 }}>Здесь появятся события — жизни, монеты и отчёты</p>
                </div>
              )}
              {!isLoading && notifications.map((notif, idx) => {
                const isUnread = !notif.isRead
                const { Icon, color, bg } = iconFor(notif)
                const isLong = (notif.body?.length ?? 0) > LONG_BODY_THRESHOLD || (notif.body?.split('\n').length ?? 0) > 4
                const isExpanded = expandedIds.has(notif.id)

                return (
                  <div
                    key={notif.id}
                    data-testid="notifications-item"
                    data-unread={isUnread ? 'true' : 'false'}
                    onClick={() => openNotification(notif)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 10px',
                      borderRadius: 14,
                      marginBottom: idx < notifications.length - 1 ? 4 : 0,
                      background: isUnread ? 'rgba(74,108,247,0.04)' : 'transparent',
                      transition: 'background 0.15s',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 11, flexShrink: 0,
                      background: bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative',
                      border: `1px solid ${color}22`,
                    }}>
                      <Icon size={17} color={color} />
                      {isUnread && (
                        <span style={{
                          position: 'absolute', top: -2, right: -2,
                          width: 8, height: 8, borderRadius: '50%',
                          background: color,
                          border: '1.5px solid white',
                        }} />
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ marginBottom: 1 }}>
                        <span style={{
                          fontSize: 13, fontWeight: isUnread ? 700 : 600,
                          color: isUnread ? '#1C2D41' : '#4A5568',
                          lineHeight: 1.25,
                        }}>
                          {stripTelegramHtml(notif.title)}
                        </span>
                      </div>
                      {notif.body && (
                        <p
                          data-testid="notifications-body"
                          style={{
                            fontSize: 12, color: '#8A97A6',
                            margin: '0 0 3px', lineHeight: 1.35,
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: isExpanded ? 'unset' : 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {renderTelegramHtml(notif.body)}
                        </p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 10, color: '#B0BAC6' }}>{formatRelative(notif.sentAt)}</span>
                        {isLong && (
                          <button
                            data-testid="notifications-expand"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(notif.id) }}
                            style={{
                              padding: 0, background: 'transparent', border: 'none', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              fontSize: 10, fontWeight: 700, color: color,
                              fontFamily: F,
                            }}
                          >
                            {isExpanded ? 'Свернуть' : 'Развернуть'}
                            <ChevronDown
                              size={11}
                              style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <button
              data-testid="notifications-mark-all"
              onClick={() => { if (canMarkAll) markAllMutation.mutate() }}
              disabled={!canMarkAll}
              style={{
                padding: '10px 16px',
                borderTop: '1px solid rgba(26,58,92,0.06)',
                borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, flexShrink: 0,
                background: 'transparent',
                cursor: canMarkAll ? 'pointer' : 'default',
                opacity: canMarkAll ? 1 : 0.55,
                width: '100%',
                fontFamily: F,
              }}
            >
              <CheckCircle2 size={13} color={canMarkAll ? '#4A6CF7' : '#9AA5B4'} />
              <span style={{ fontSize: 12, color: canMarkAll ? '#4A6CF7' : '#9AA5B4', fontWeight: canMarkAll ? 600 : 400 }}>
                {markAllMutation.isPending ? 'Отмечаем…' : 'Отметить все как прочитанные'}
              </span>
            </button>
          </motion.div>

          {/* ── Full-screen popup view ── */}
          <AnimatePresence>
            {popupNotif && (() => {
              const { Icon, color, bg } = iconFor(popupNotif)
              return (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setPopupNotif(null)}
                  style={{
                    position: 'fixed', inset: 0, zIndex: 1100,
                    background: 'rgba(10,18,40,0.55)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20,
                  }}
                >
                  <motion.div
                    data-testid="notification-popup"
                    initial={{ scale: 0.88, opacity: 0, y: 16 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.92, opacity: 0, y: 8 }}
                    transition={{ type: 'spring', damping: 22, stiffness: 280 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%', maxWidth: 380, maxHeight: 'calc(100dvh - 80px)',
                      background: 'white', borderRadius: 24,
                      boxShadow: '0 28px 72px rgba(26,58,92,0.32), 0 0 0 1px rgba(255,255,255,0.6)',
                      display: 'flex', flexDirection: 'column',
                      overflow: 'hidden',
                      fontFamily: F,
                    }}
                  >
                    {/* Popup header */}
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '20px 20px 14px',
                      borderBottom: '1px solid rgba(26,58,92,0.07)',
                    }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 14,
                        background: bg, border: `1px solid ${color}33`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon size={24} color={color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 style={{
                          fontSize: 16, fontWeight: 800, color: '#1C2D41',
                          margin: '0 0 4px', lineHeight: 1.3,
                        }}>
                          {popupNotif.title}
                        </h2>
                        <span style={{ fontSize: 11, color: '#9AA5B4' }}>
                          {formatFullDate(popupNotif.sentAt)}
                        </span>
                      </div>
                      <button
                        data-testid="notification-popup-close"
                        onClick={() => setPopupNotif(null)}
                        style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.06)', border: 'none',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        <X size={15} color="#6B7B8D" />
                      </button>
                    </div>

                    {/* Popup body — full text, scrollable */}
                    <div style={{
                      padding: '18px 20px 22px',
                      overflowY: 'auto',
                      flex: 1,
                    }}>
                      <p style={{
                        fontSize: 14, color: '#3D4A5C',
                        margin: 0, lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {popupNotif.body ? renderTelegramHtml(popupNotif.body) : '—'}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              )
            })()}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  )
}
